// Copyright aptihealth, inc. 2019 All Rights Reserved

import moment from "moment-timezone";
import React, { Component } from "react";
import { connect } from "react-redux";
import { api } from "../../../APIRequests";
import { PatientPaymentBanner } from "../../../component-library";
import ScheduleWidgets from "../../../components/Common/Schedule/ScheduleWidgets";
import { ScheduleSuccess } from "../../../components/Patient/Schedule";
import CardSecondary from "../../../components/UI/Card/CardSecondary";
import { showAlertWithAction } from "../../../redux/actions/alerts";
import { getPatientProfile } from "../../../redux/actions/auth";
import { getQueryParams } from "../../../utils/filters";
import { getScheduleForToday, isCareTeamRestricted } from "../../../utils/scheduleUtils";
import PatientCareTeamSchedule from "../../../components/Patient/Schedule/PatientCareTeam";
import {
  DesktopCareTeamLoader,
  MobileCareTeamLoader,
} from "../../../components/Common/Loaders/CareTeamLoader";

/**
 * This component is used in two different ways:
 *  1. Creating new schedule with `mode: 'new'`
 *  2. Rescheduling/editing with `mode: 'edit'`
 *
 *  Note: `mode` is passed as `location.state.mode` property via in react-router.
 *        Reschedule will require a queryParam `provider-id` to reschedule with same provider
 */

// Ninety day care status
const NOT_ASSIGNED = "not_assigned";

class Schedule extends Component {
  constructor(props) {
    super(props);
    let mode = getQueryParams("mode", this.props.location.search);

    if (!mode) {
      props.history.push("/app");
    } else {
      /** TODO: revert back provider-id to queryParams */
      let providerId = getQueryParams("provider_id", this.props.location.search) || null;
      let call_id = getQueryParams("call_id", this.props.location.search) || null;

      this.state = {
        mode,
        providerId,
        call_id,
        timeSlots: null,
        dateStr: "",
        selectedTimeSlot: null,
        timeSlotsLoading: true,
        isDisabled: true,
        slotsCount: 0,
        selectedDate: null,
        appointmentDetail: null,
        patientDetails: null,
        earliestDateAllowed: null,
      };
    }
  }

  getAvailableSchedules = (dateString, providerId) => {
    let urlParams = {
      dateString,
      tz: this.props.timezone,
      providerId,
      call_id: getQueryParams("call_id", this.props.location.search),
    };
    const options = { params: { showLoader: true } };

    return api.shared.available_schedules({ urlParams, options });
  };

  scheduleCall = (data) => {
    let { date, time } = data;
    const { timezone } = this.props;

    switch (this.state.mode) {
      case "new": {
        return api.schedule.post_schedule({
          data: {
            ...data,
            timezone,
            timestamp: `${date} ${time}`,
          },
        });
      }

      case "edit": {
        return api.schedule.put_schedule({
          data: {
            ...data,
            timezone,
            call_id: this.state.call_id,
            timestamp: `${date} ${time}`,
          },
        });
      }

      default: {
      }
    }
  };

  onTimeSlotClick = (slotValue) => {
    this.setState({
      selectedTimeSlot: slotValue,
      isDisabled: false,
    });
  };

  onConfirmSlotSubmit = () => {
    let localTZ = this.props.timezone;
    let combinedDateTime = this.state.selectedDate + " " + this.state.selectedTimeSlot?.time_slot;
    let UTCDate = moment
      .tz(combinedDateTime, "YYYY-MM-DD hh:mm a", localTZ)
      .utc()
      .format("YYYY-MM-DD");
    let UTCTime = moment.tz(combinedDateTime, "YYYY-MM-DD hh:mm a", localTZ).utc().format("HH:mm");
    let local_date_time = moment
      .tz(combinedDateTime, "YYYY-MM-DD hh:mm a", localTZ)
      .format("YYYY-MM-DD HH:mm");
    let data = {
      date: UTCDate,
      time: UTCTime,
      provider_id: this.state.selectedTimeSlot?.provider_id,
      local_date_time,
    };

    if (this.state.providerType) {
      if (this.state.providerType === "Prescriber") {
        data.provider_type = "PRESCRIBE";
      } else {
        data.provider_type = "BEHAVIORAL";
      }
    } else {
      data.provider_type = "BEHAVIORAL_INTAKE";
    }

    this.scheduleCall(data)
      .then((cleanResponse) => {
        let appointmentDetail = cleanResponse.appointment_details;

        this.setState({
          appointmentDetail: appointmentDetail || "rescheduled",
        });

        this.props.getPatientProfile();
      })
      .catch((err) => {
        this.props.showAlertWithAction(err.message);
        console.log(err);
      });
  };

  relevantProviderTypes = ["Behavioral Specialist", "Prescriber"];

  fetchCall = async () => {
    const scheduleCallList = await getScheduleForToday();

    return scheduleCallList.schedule.find((call) => call.callId === this.state.call_id);
  };

  async getEarliestDateAllowedToSchedule(providerType) {
    const queryParams = {
      providerType: providerType,
      tz: this.props.timezone,
    };
    const earliestDateAllowedResponse = await api.shared.get_earliest_date_allowed_to_schedule({
      queryParams,
    });

    return earliestDateAllowedResponse?.earliest_date_allowed;
  }

  async componentDidMount() {
    const call = this.state.call_id ? await this.fetchCall() : null;
    const careTeamMembers = this.props.patientProfile.care_team_members.filter((member) =>
      this.relevantProviderTypes.includes(member.type),
    );
    let defaultProviderId = getQueryParams("provider_id", this.props.location.search) || undefined;
    let defaultProviderType;

    const availableCareTeamMembers = careTeamMembers.map((member) => {
      const newMember = { ...member };

      newMember["disabled"] = isCareTeamRestricted(member["type"], this.props.patientProfile);
      if (!defaultProviderId && !newMember["disabled"]) {
        defaultProviderId = newMember?.provider_id;
        defaultProviderType = newMember?.type;
      } else if (
        defaultProviderId === newMember?.provider_id &&
        defaultProviderType === undefined
      ) {
        defaultProviderType = newMember?.type;
      }

      return newMember;
    });

    const earliestDateAllowed = await this.getEarliestDateAllowedToSchedule(defaultProviderType);

    this.setState(
      {
        providerId: defaultProviderId,
        providerType: defaultProviderType,
        careTeamMembers: availableCareTeamMembers,
        patientDetails: this.props.patientProfile,
        call,
        earliestDateAllowed,
      },
      () => this.resetDaySelection(),
    );
  }

  careTeamMemberSelect = (providerId, providerType) => {
    let newState = { providerId: providerId };

    if (providerType) {
      newState["providerType"] = providerType;
    }
    this.setState(newState);
  };

  async componentDidUpdate(prevProps, prevState) {
    if (prevState.providerId && prevState.providerId !== this.state.providerId) {
      await this.resetDaySelection();
    }
  }

  async resetDaySelection() {
    const searchDate = this.state.selectedDate || this.state.earliestDateAllowed;

    if (searchDate) {
      daySelectHandler(searchDate, this);
    }
  }

  renderCareTeamSection = () => {
    return (
      <div className="Schedule__profile-info col-12 col-lg-10 p-lg-4 pb-lg-5">
        <CardSecondary className="py-4 px-3 px-lg-4">
          <div style={{ minHeight: "110px" }}>
            <p className="fs-18 pb-1">
              <strong>Care Team</strong>
            </p>
            <hr />
            {this.state.careTeamMembers && this.state.careTeamMembers.length > 0 ? (
              <PatientCareTeamSchedule
                providerId={this.state.providerId}
                careTeamMembers={this.state.careTeamMembers}
                onMemberSelect={this.careTeamMemberSelect}
                patientDetails={this.state.patientDetails}
              />
            ) : (
              <div>
                <DesktopCareTeamLoader classes="d-lg-block d-none" />
                <MobileCareTeamLoader classes="d-block d-lg-none" />
              </div>
            )}
          </div>
        </CardSecondary>
      </div>
    );
  };

  render() {
    const { patientProfile, patientCardInfo, flags } = this.props;
    const { showPaywall } = patientCardInfo ?? {};

    if (patientProfile.status === "INACTIVE") {
      this.props.history.push("/app/schedule/inactive");
    }

    let timeSlotConf = {
      selectedTimeSlot: this.state.selectedTimeSlot,
      isDisabled: this.state.isDisabled,
      selectedDate: this.state.selectedDate,
    };

    let showCareTeamSection = false;

    /** checking if to show care team section */
    if (patientProfile && patientProfile.ninety_day_care !== NOT_ASSIGNED) {
      showCareTeamSection = true;
    }

    return (
      <div data-public="true">
        {showPaywall && <PatientPaymentBanner className="PatientPaymentBanner-schedule" />}
        <div className="Schedule__wpr p-lg-4 mx-auto row no-gutters">
          {this.state.appointmentDetail && (
            <ScheduleSuccess behavioralSpecialist={this.state.appointmentDetail.provider_name} />
          )}
          {showCareTeamSection && this.renderCareTeamSection()}
          <ScheduleWidgets
            timeSlotsLoading={this.state.timeSlotsLoading}
            timeSlots={this.state.timeSlots}
            that={this}
            timeSlotConf={timeSlotConf}
            timezone={this.props.timezone}
            onTimeSlotClick={this.onTimeSlotClick}
            onConfirmSlotSubmit={this.onConfirmSlotSubmit}
            dateStr={this.state.dateStr}
            selectedDate={this.state.selectedDate && moment(this.state.selectedDate)}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    timezone: state.auth.timezone,
    patientProfile: state.auth.profile,
    patientCardInfo: state.patientCardInfo,
    flags: state.flags,
  };
};

export default connect(mapStateToProps, { getPatientProfile, showAlertWithAction })(Schedule);

export const monthName = [
  "",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "June",
  "July",
  "Aug",
  "Sept",
  "Oct",
  "Nov",
  "Dec",
];

export const daySelectHandler = (dateString, component) => {
  // Extract month and date from the date string (YYYY-MM-DD format)
  const month = Number(dateString.substring(5, 7));
  const date = Number(dateString.substring(8, 11));
  const formattedDateStr = `${date} ${monthName[month]}`;

  if (moment(dateString).isBefore(moment(component.state.earliestDateAllowed))) {
    component.setState({
      dateStr: formattedDateStr,
      isDisabled: true,
      selectedDate: dateString,
      selectedTimeSlot: null,
      timeSlots: [],
      timeSlotsLoading: false,
    });

    return;
  }

  // Update component state with selected date and reset time slot related state
  component.setState({
    dateStr: formattedDateStr,
    isDisabled: true,
    selectedDate: dateString,
    selectedTimeSlot: null,
    timeSlots: null,
    timeSlotsLoading: true,
  });

  // Fetch available time slots for the selected date
  component
    .getAvailableSchedules(dateString, component.state.providerId)
    .then((cleanResponse) => {
      let timeSlots = cleanResponse.time_slots;

      // Update component state with fetched time slots
      component.setState({
        timeSlots: timeSlots,
        timeSlotsLoading: false,
      });
    })
    .catch((err) => {
      console.log(err);
    });
};
